import React from 'react';
import { NodeType, DelegationType } from './types.js';
import { 
  TriggerIcon, 
  SendEmailIcon, 
  GenerateDocumentIcon, 
  FileTaxesIcon, 
  UpdateCrmIcon, 
  SocialPostIcon, 
  WaitIcon, 
  IfConditionIcon, 
  LoopStartIcon, 
  LoopEndIcon, 
  CodeIcon, 
  DatabaseIcon, 
  ApiIcon, 
  LoggerIcon, 
  BrowsingAgentIcon, 
  RegisterBusinessIcon, 
  ExpandBusinessIcon, 
  DashboardIcon, 
  CreateWebsiteIcon, 
  ManageInvoicesIcon, 
  MarketResearchIcon, 
  ComplianceCheckIcon, 
  HireEmployeeIcon,
  MessageIcon,
  CommentIcon
} from './components/icons';

import ChecklistIcon from './components/icons/ChecklistIcon';

export const NODE_WIDTH = 208; // Corresponds to w-52 class
export const NODE_HEIGHT = 80; // Corresponds to h-20 class

// Default delegation configuration for all nodes
const defaultDelegationConfig = {
  delegationType: DelegationType.NONE,
  delegateToHuman: {
    contactId: '',
    contactName: '',
    instructions: '',
    expectedResponseTime: 0
  },
  delegateToAgent: {
    agentId: '',
    agentName: '',
    configuration: {}
  }
};

export const NODE_TYPES = {
  [NodeType.TRIGGER]: {
    type: NodeType.TRIGGER,
    label: 'Trigger',
    icon: <TriggerIcon className="w-6 h-6" />,
    defaultData: { condition: 'On New User Signup' },
    description: 'Starts the workflow based on a condition.'
  },
  [NodeType.SEND_EMAIL]: {
    type: NodeType.SEND_EMAIL,
    label: 'Send Email',
    icon: <SendEmailIcon className="w-6 h-6" />,
    defaultData: { to: '{{user.email}}', subject: 'Welcome!', body: 'Thank you for signing up.' },
    description: 'Sends an email to a recipient.'
  },
  [NodeType.SEND_MESSAGE]: {
    type: NodeType.SEND_MESSAGE,
    label: 'Send Message',
    icon: <MessageIcon className="w-6 h-6" />,
    defaultData: { to: '{{user.phone}}', message: 'Welcome to our service!' },
    description: 'Sends a message to a recipient.'
  },
  [NodeType.GENERATE_DOCUMENT]: {
    type: NodeType.GENERATE_DOCUMENT,
    label: 'Generate Document',
    icon: <GenerateDocumentIcon className="w-6 h-6" />,
    defaultData: { template: 'contract', fields: {} },
    description: 'Generates a document from a template.'
  },
  [NodeType.WAIT]: {
    type: NodeType.WAIT,
    label: 'Wait',
    icon: <WaitIcon className="w-6 h-6" />,
    defaultData: { duration: 1, unit: 'hours' },
    description: 'Pauses the workflow for a specified time.'
  },
  [NodeType.IF_CONDITION]: {
    type: NodeType.IF_CONDITION,
    label: 'If Condition',
    icon: <IfConditionIcon className="w-6 h-6" />,
    defaultData: { condition: '{{user.age}} >= 18', trueBranch: '', falseBranch: '' },
    description: 'Branches the workflow based on a condition.'
  },
  [NodeType.LOOP_START]: {
    type: NodeType.LOOP_START,
    label: 'Loop Start',
    icon: <LoopStartIcon className="w-6 h-6" />,
    defaultData: { collection: '{{users}}', itemVariable: 'user' },
    description: 'Starts a loop over a collection.'
  },
  [NodeType.LOOP_END]: {
    type: NodeType.LOOP_END,
    label: 'Loop End',
    icon: <LoopEndIcon className="w-6 h-6" />,
    defaultData: {},
    description: 'Ends a loop.'
  },
  [NodeType.CODE]: {
    type: NodeType.CODE,
    label: 'Code',
    icon: <CodeIcon className="w-6 h-6" />,
    defaultData: { language: 'javascript', code: '// Your code here' },
    description: 'Executes custom code.'
  },
  [NodeType.QUERY_DATABASE]: {
    type: NodeType.QUERY_DATABASE,
    label: 'Query Database',
    icon: <DatabaseIcon className="w-6 h-6" />,
    defaultData: { query: 'SELECT * FROM users WHERE active = true' },
    description: 'Queries a database.'
  },
  [NodeType.CONNECT_API]: {
    type: NodeType.CONNECT_API,
    label: 'Connect API',
    icon: <ApiIcon className="w-6 h-6" />,
    defaultData: { url: 'https://api.example.com/data', method: 'GET' },
    description: 'Connects to an external API.'
  },
  [NodeType.LOGGER]: {
    type: NodeType.LOGGER,
    label: 'Logger',
    icon: <LoggerIcon className="w-6 h-6" />,
    defaultData: { message: 'Workflow reached this point' },
    description: 'Logs a message for debugging.'
  },
  [NodeType.BROWSING_AGENT]: {
    type: NodeType.BROWSING_AGENT,
    label: 'Browsing Agent',
    icon: <BrowsingAgentIcon className="w-6 h-6" />,
    defaultData: { task: 'Research competitors in the AI industry' },
    description: 'Uses AI to browse and research information.'
  },
  [NodeType.REGISTER_BUSINESS]: {
    type: NodeType.REGISTER_BUSINESS,
    label: 'Register Business',
    icon: <RegisterBusinessIcon className="w-6 h-6" />,
    defaultData: { jurisdiction: 'Delaware', businessType: 'LLC' },
    description: 'Automates steps in business registration.'
  },
  [NodeType.HIRE_EMPLOYEE]: {
    type: NodeType.HIRE_EMPLOYEE,
    label: 'Hire Employee',
    icon: <HireEmployeeIcon className="w-6 h-6" />,
    defaultData: { role: 'Software Engineer', status: 'Send Offer Letter' },
    description: 'Automates steps in the hiring process.'
  },
  [NodeType.EXPAND_BUSINESS]: {
    type: NodeType.EXPAND_BUSINESS,
    label: 'Expand Business',
    icon: <ExpandBusinessIcon className="w-6 h-6" />,
    defaultData: { action: 'Open new location in {{city}}' },
    description: 'Automates business expansion steps.'
  },
  [NodeType.CREATE_WEBSITE]: {
    type: NodeType.CREATE_WEBSITE,
    label: 'Create Website',
    icon: <CreateWebsiteIcon className="w-6 h-6" />,
    defaultData: { platform: 'WordPress', domain: '{{business.name}}.com' },
    description: 'Creates a website for the business.'
  },
  [NodeType.GENERATE_DOCUMENT]: {
    type: NodeType.GENERATE_DOCUMENT,
    label: 'Generate Document',
    icon: <GenerateDocumentIcon className="w-6 h-6" />,
    defaultData: { template: 'contract', fields: {} },
    description: 'Generates a document from a template.'
  },
  [NodeType.MANAGE_INVOICES]: {
    type: NodeType.MANAGE_INVOICES,
    label: 'Manage Invoices',
    icon: <ManageInvoicesIcon className="w-6 h-6" />,
    defaultData: { action: 'Send invoice to {{client.name}}' },
    description: 'Manages invoicing and payments.'
  },
  [NodeType.FILE_TAXES]: {
    type: NodeType.FILE_TAXES,
    label: 'File Taxes',
    icon: <FileTaxesIcon className="w-6 h-6" />,
    defaultData: { country: 'USA', year: new Date().getFullYear(), form: '1040' },
    description: 'Prepares and files tax documents.'
  },
  [NodeType.UPDATE_CRM]: {
    type: NodeType.UPDATE_CRM,
    label: 'Update CRM',
    icon: <UpdateCrmIcon className="w-6 h-6" />,
    defaultData: { record: '{{user.id}}', status: 'Onboarded' },
    description: 'Updates a record in your CRM.'
  },
  [NodeType.SOCIAL_POST]: {
    type: NodeType.SOCIAL_POST,
    label: 'Social Post',
    icon: <SocialPostIcon className="w-6 h-6" />,
    defaultData: { platform: 'Twitter', content: 'Check out our new feature!' },
    description: 'Posts content to social media.'
  },
  [NodeType.DASHBOARD]: {
    type: NodeType.DASHBOARD,
    label: 'Dashboard',
    icon: <DashboardIcon className="w-6 h-6" />,
    defaultData: { metrics: ['revenue', 'users'] },
    description: 'Displays key metrics and analytics.'
  },
  [NodeType.MARKET_RESEARCH]: {
    type: NodeType.MARKET_RESEARCH,
    label: 'Market Research',
    icon: <MarketResearchIcon className="w-6 h-6" />,
    defaultData: { topic: 'Competitor analysis for SaaS products', market: 'Global' },
    description: 'Uses AI to perform market research.'
  },
  [NodeType.COMPLIANCE_CHECK]: {
    type: NodeType.COMPLIANCE_CHECK,
    label: 'Compliance Check',
    icon: <ComplianceCheckIcon className="w-6 h-6" />,
    defaultData: { checkType: 'GDPR', region: 'EU' },
    description: 'Checks for business compliance in a region.'
  },
  [NodeType.HIRE_EMPLOYEE]: {
    type: NodeType.HIRE_EMPLOYEE,
    label: 'Hire Employee',
    icon: <HireEmployeeIcon className="w-6 h-6" />,
    defaultData: { role: 'Software Engineer', status: 'Send Offer Letter' },
    description: 'Automates steps in the hiring process.'
  },
  [NodeType.FILE_TAXES]: {
    type: NodeType.FILE_TAXES,
    label: 'File Taxes',
    icon: <FileTaxesIcon className="w-6 h-6" />,
    defaultData: { country: 'USA', year: new Date().getFullYear(), form: '1040' },
    description: 'Prepares and files tax documents.'
  },
  [NodeType.UPDATE_CRM]: {
    type: NodeType.UPDATE_CRM,
    label: 'Update CRM',
    icon: <UpdateCrmIcon className="w-6 h-6" />,
    defaultData: { record: '{{user.id}}', status: 'Onboarded' },
    description: 'Updates a record in your CRM.'
  },
  [NodeType.SOCIAL_POST]: {
    type: NodeType.SOCIAL_POST,
    label: 'Social Post',
    icon: <SocialPostIcon className="w-6 h-6" />,
    defaultData: { platform: 'Twitter', content: 'Check out our new feature!' },
    description: 'Posts content to social media.'
  },
  [NodeType.COMMENT]: {
    type: NodeType.COMMENT,
    label: 'Comment',
    icon: <CommentIcon className="w-6 h-6" />,
    defaultData: { text: 'Add your notes here' },
    description: 'Add comments to your workflow.'
  },
  // Add TODO_LIST node type
  ['TODO_LIST']: {
    type: 'TODO_LIST',
    label: 'Todo List',
    icon: <ChecklistIcon className="w-6 h-6" />,
    defaultData: { 
      items: [
        { text: 'First task', completed: false },
        { text: 'Second task', completed: false }
      ],
      priority: 'medium',
      assignedTo: 'Unassigned'
    },
    description: 'Create a todo list for tasks.'
  },
  // Add TODO_VIEW node type
  ['TODO_VIEW']: {
    type: 'TODO_VIEW',
    label: 'Todo View',
    icon: <ChecklistIcon className="w-6 h-6" />,
    defaultData: { filter: 'all' },
    description: 'View and manage todo items.'
  }
};